import React, { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControlLabel, Switch
} from '@mui/material'
import hipersendService from '../../services/hipersend'
import Text from '../Text'
import OutcomeChip from './OutcomeChip'

const sf = hipersendService.smartFilter

const toInt = (v, d) => { const n = parseInt(v, 10); return isFinite(n) ? n : d }
const toNum = (v, d) => { const n = Number(String(v).replace(',', '.')); return isFinite(n) ? n : d }

// Receitas com valor(es) ajustável(is). apply() recebe as conditions (clonadas)
// e os valores do formulário, e grava cada valor no lugar certo das conditions.
// As receitas que não estão aqui são instaladas como vêm (1 clique).
const RECIPE_PARAMS = {
  'max-por-dia': {
    fields: [{ key: 'n', label: 'Máximo por número/dia', def: 3, min: 1 }],
    apply: (c, v) => { c.items[0].value = toInt(v.n, 3) }
  },
  'intervalo-minimo': {
    fields: [{ key: 'n', label: 'Horas de intervalo', def: 6, min: 1 }],
    apply: (c, v) => { c.items[0].value = toInt(v.n, 6) }
  },
  'nao-cobrar-cedo': {
    fields: [{ key: 'n', label: 'Dias de antecedência', def: 3, min: 1 }],
    apply: (c, v) => { c.items[1].value = toInt(v.n, 3) }
  },
  'parar-cobranca-inadimplencia-antiga': {
    fields: [{ key: 'n', label: 'Meses de atraso para bloquear', def: 6, min: 1 }],
    apply: (c, v) => { c.items[0].value = toInt(v.n, 6) }
  },
  'valor-minimo': {
    fields: [{ key: 'n', label: 'Valor mínimo (R$)', def: 5, min: 0, step: '0.01' }],
    apply: (c, v) => { c.items[0].value = toNum(v.n, 5) }
  },
  'cobranca-alta-aprovacao': {
    fields: [{ key: 'n', label: 'A partir de (R$)', def: 500, min: 0, step: '0.01' }],
    apply: (c, v) => { c.items[1].value = toNum(v.n, 500) }
  },
  'janela-d2-d5': {
    fields: [
      { key: 'antes', label: 'Dias antes do vencimento', def: 2, min: 0 },
      { key: 'depois', label: 'Dias depois do vencimento', def: 5, min: 0 }
    ],
    // daysToDueDate entre [-depois, antes]: positivo = vence em X, negativo = vencida há X
    apply: (c, v) => { c.items[0].value = [-toInt(v.depois, 5), toInt(v.antes, 2)] }
  },
  'nada-de-madrugada': {
    fields: [
      { key: 'from', label: 'Início (hora)', def: 8, min: 0, max: 23 },
      { key: 'to', label: 'Fim (hora)', def: 20, min: 0, max: 23 }
    ],
    apply: (c, v) => { c.items[0].value = toInt(v.from, 8); c.items[1].value = toInt(v.to, 20) }
  },
  'promo-horario-comercial': {
    fields: [
      { key: 'from', label: 'Início (hora)', def: 8, min: 0, max: 23 },
      { key: 'to', label: 'Fim (hora)', def: 20, min: 0, max: 23 }
    ],
    apply: (c, v) => { c.items[1].value = [toInt(v.from, 8), toInt(v.to, 20)] }
  }
}

// Dialogo "Ajustar e ativar" disparado ao instalar uma receita. Importa o template
// (cria a regra com os defaults, desativada), aplica os valores ajustados e — se o
// usuário pediu — ja deixa ativa, sem precisar passar pela aba Regras.
export default function RecipeInstaller({ template, api, secret, onClose, onDone, notify }) {
  const spec = template ? RECIPE_PARAMS[template.code] : null
  const [values, setValues] = useState({})
  const [activate, setActivate] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!template) return
    const init = {}
    if (spec) spec.fields.forEach((f) => { init[f.key] = f.def })
    setValues(init)
    setActivate(true)
  }, [template]) // eslint-disable-line react-hooks/exhaustive-deps

  const isAllow = template?.action?.type === 'ALLOW'

  const confirm = async () => {
    if (!template) return
    setSaving(true)
    try {
      const created = await sf.importTemplate(api, secret, template.code)
      if (created && created._id) {
        const patch = {}
        if (spec) {
          const conditions = JSON.parse(JSON.stringify(created.conditions || { op: 'AND', items: [] }))
          spec.apply(conditions, values)
          patch.conditions = conditions
        }
        if (activate) patch.enabled = true
        if (Object.keys(patch).length) await sf.updateRule(api, secret, created._id, patch)
      }
      await onDone()
      notify(activate ? 'Receita instalada e ativada' : 'Receita instalada (desativada)')
      onClose()
    } catch (e) {
      notify('Falha ao instalar a receita')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!template} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
      {template && (
        <>
          <DialogTitle>
            <div className="flex items-center justify-between gap-2">
              <span style={{ fontSize: 15, fontWeight: 600 }}>{template.name}</span>
              <OutcomeChip type={template.action?.type} />
            </div>
          </DialogTitle>
          <DialogContent dividers sx={{ px: 3, py: 3 }}>
            <div className="space-y-4">
              <Text className="block text-sm opacity-70" style={{ lineHeight: 1.45 }}>{template.description}</Text>

              {spec && (
                <div className="flex gap-2 flex-wrap">
                  {spec.fields.map((f) => (
                    <TextField
                      key={f.key} size="small" type="number" label={f.label}
                      value={values[f.key] ?? f.def}
                      inputProps={{ min: f.min, max: f.max, step: f.step || 1 }}
                      onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}
                      style={{ width: spec.fields.length > 1 ? 160 : '100%' }}
                    />
                  ))}
                </div>
              )}

              <div>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={<Switch checked={activate} onChange={(e) => setActivate(e.target.checked)} />}
                  label={<span className="text-sm">Ativar agora</span>}
                />
                {isAllow && (
                  <Text className="block text-xs mt-1" style={{ color: '#b54708', lineHeight: 1.4 }}>
                    Esta receita <b>libera</b> mensagens — ela só faz efeito no modo lista-branca
                    ("enviar só o que as regras permitirem"), em Configurações.
                  </Text>
                )}
                {!activate && (
                  <Text className="block text-xs opacity-60 mt-1">
                    Vai ser criada desativada — você liga quando quiser na aba Regras.
                  </Text>
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button variant="contained" disableElevation onClick={confirm} disabled={saving} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
              {saving ? 'Instalando…' : (activate ? 'Instalar e ativar' : 'Instalar')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
